import LeaderboardStats from './LeaderboardStats';
import React, { useEffect, useState } from "react";
import { DateTime } from "luxon";
import Leaderboard from "./Leaderboard";
import { LeaderboardFrame, DateSelector, formatLeaderboardDate, fetchGameStatuses } from "@precisionpros/game-ui";
import { connectProGame } from "./ConnectProBrand";

const API_URL = process.env.REACT_APP_API_URL;
const ARIZONA = "America/Phoenix";

const getHomeUrl = () => {
  const isLocalDev = API_URL.includes("localhost") || API_URL.includes("127.0.0.1");
  if (isLocalDev) {
    const url = new URL(API_URL);
    return `${url.protocol}//${url.hostname}:3000`;
  }
  return "https://games.precisionpros.com";
};

const getOtherGameFrontends = () => {
  const isLocalDev = API_URL.includes("localhost") || API_URL.includes("127.0.0.1");
  if (isLocalDev) {
    return {
      wordpro: "http://localhost:3005",
      eventpro: "http://localhost:3002",
      buildpro: "http://localhost:3003",
      wordprofinder: "http://localhost:3004",
      seashroud: "http://localhost:3006",
      mathpro: "http://localhost:3008",
    };
  }
  return {
      wordpro: "https://wordpro.precisionpros.com",
    eventpro: "https://eventpro.precisionpros.com",
    buildpro: "https://buildpro.precisionpros.com",
    wordprofinder: "https://wordprofinder.precisionpros.com",
    seashroud: "https://seashroud.precisionpros.com",
    mathpro: "https://mathpro.precisionpros.com",
  };
};

const getOtherGameApis = () => {
  const isLocalDev = API_URL.includes("localhost") || API_URL.includes("127.0.0.1");
  if (isLocalDev) {
    return {
      wordpro: "http://localhost:8006",
      eventpro: "http://localhost:8003",
      buildpro: "http://localhost:8004",
      wordprofinder: "http://localhost:8005/api/wordprofinder",
    };
  }
  return {
      wordpro: "https://wordpro.precisionpros.com",
    eventpro: "https://eventpro.precisionpros.com",
    buildpro: "https://buildpro.precisionpros.com",
    wordprofinder: "https://wordprofinder.precisionpros.com/api/wordprofinder",
  };
};

const LeaderboardPage = () => {
  const currentPlayerName = new URLSearchParams(window.location.search).get("player_name") || localStorage.getItem("playerName") || "";
  const [date, setDate] = useState(() =>
    DateTime.now().setZone(ARIZONA).startOf("day")
  );
  const [scores, setScores] = useState([]);
  const [hasPlayedToday, setHasPlayedToday] = useState(null);
  const [otherGamesStatus, setOtherGamesStatus] = useState({});
  const [statusReady, setStatusReady] = useState(false);

  useEffect(() => {
    const hasHubToken = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken');
    if (!currentPlayerName && !hasHubToken) {
      setHasPlayedToday(null);
      setOtherGamesStatus({});
      setStatusReady(true);
      return;
    }

    setStatusReady(false);
    fetchGameStatuses()
      .then((statuses) => {
        setOtherGamesStatus(statuses);
        setHasPlayedToday(statuses.connectpro === true);
      })
      .catch((err) => {
        console.error("Error checking game statuses:", err);
        setOtherGamesStatus({});
        setHasPlayedToday(null);
      })
      .finally(() => setStatusReady(true));
  }, [currentPlayerName]);

  useEffect(() => {
    const todayDate = DateTime.now().setZone(ARIZONA).startOf("day");
    const isViewingToday = date.hasSame(todayDate, "day");

    if (isViewingToday && !statusReady) {
      setScores([]);
      return;
    }

    // Don't fetch today's scores if player hasn't played yet, but still show weekly/all-time stats
    if (isViewingToday && hasPlayedToday !== true) {
      setScores([]);
    } else {
      fetchScoresForDate(date);
    }
  }, [date, hasPlayedToday, statusReady]);

  const fetchScoresForDate = async (dt) => {
    const dateStr = dt.toISODate();
    const url = `${API_URL}/connectpro/leaderboard/?date=${dateStr}&limit=100&offset=0`;
    console.log("🎯 Fetching leaderboard from:", url);
    setScores([]);
    try {
      const res = await fetch(url);
      console.log("📡 Response status:", res.status);
      if (!res.ok) {
        console.error("❌ Response not OK:", res.status, res.statusText);
        throw new Error(`Leaderboard request failed: ${res.status}`);
      }
      const data = await res.json();
      console.log("📊 Leaderboard response:", data);
      setScores(data.results || []);
    } catch (err) {
      console.error("❌ Error loading leaderboard:", err);
      setScores([]);
    }
  };

  const changeDate = (days) => {
    setDate((prev) => prev.plus({ days }));
  };

  const isToday = date.hasSame(DateTime.now().setZone(ARIZONA), "day");
  const shouldHideResults = isToday && hasPlayedToday !== true;

  const frontends = getOtherGameFrontends();
  const links = [
    { key: "wordpro", letter: "W", color: "#4CAF50", href: `${frontends.wordpro}${otherGamesStatus.wordpro ? "/leaderboard" : "/"}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ""}`, played: !!otherGamesStatus.wordpro },
    { key: "connectpro", letter: "C", color: "#2563eb", href: `${otherGamesStatus.connectpro ? "/leaderboard" : "/"}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ""}`, played: !!otherGamesStatus.connectpro },
    { key: "eventpro", letter: "E", color: "#ea580c", href: `${frontends.eventpro}${otherGamesStatus.eventpro ? "/leaderboard" : "/"}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ""}`, played: !!otherGamesStatus.eventpro },
    { key: "buildpro", letter: "B", color: "#0f766e", href: `${frontends.buildpro}${otherGamesStatus.buildpro ? "/leaderboard" : "/"}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ""}`, played: !!otherGamesStatus.buildpro },
    { key: "wordprofinder", letter: "WF", color: "#4CAF50", href: `${frontends.wordprofinder}${otherGamesStatus.wordprofinder ? "/leaderboard" : "/"}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ""}`, played: !!otherGamesStatus.wordprofinder },
    { key: "seashroud", letter: "S", color: "#287f89", href: `${frontends.seashroud}${otherGamesStatus.seashroud ? "/leaderboard" : "/"}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ""}`, played: !!otherGamesStatus.seashroud },
    { key: "mathpro", letter: "M", color: "#996128", href: `${frontends.mathpro}${otherGamesStatus.mathpro ? "/leaderboard" : "/"}${currentPlayerName ? `?player_name=${encodeURIComponent(currentPlayerName)}` : ""}`, played: !!otherGamesStatus.mathpro },
  ];
  const displayedLinks = statusReady ? links : [];

  return (
    <LeaderboardFrame game={connectProGame} currentGameKey="connectpro" title="Leaderboard" links={displayedLinks} homeHref={getHomeUrl()} showPrePlayBanner={shouldHideResults} playHref={`${window.location.origin}/`} howToPlayHref={`${window.location.origin}/how-to-play`} dateSelector={<DateSelector dateLabel={formatLeaderboardDate(date.toISODate())} onPrevious={() => changeDate(-1)} onNext={() => changeDate(1)} />}>

        <h2>Today’s Results</h2>
        {scores.length > 0 ? (
          <Leaderboard
            leaderboard={scores}
            selectedDate={date.toISODate()}
            hasPlayedToday={hasPlayedToday}
          />
        ) : (
          <div style={{ backgroundColor: "white", borderRadius: "12px", padding: "32px 20px", textAlign: "center", color: "#888", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
            No scores for this day.
          </div>
        )}

        <LeaderboardStats endpoint={`${API_URL}/connectpro/leaderboard-records/`} date={date.toISODate()} />
    </LeaderboardFrame>
  );
};

export default LeaderboardPage;
